// Turkish money input formatting + parsing.
//   parseMoney("5.490.000")            -> 5490000
//   parseMoney("5 490 000")            -> 5490000
//   parseMoney("₺5.490.000")           -> 5490000
//   parseMoney("5,490,000")            -> 5490000  (external clipboard)
//   parseMoney("1.234,56")             -> 1234.56  (tr-TR decimal)
//   parseMoney("1234.56")              -> 1234.56  (already decimal)
//   parseMoney("")                     -> null
//   parseMoney(null)                   -> null
//   formatMoneyGroups(5490000)         -> "5.490.000"
//   formatMoneyGroups(1234.56)         -> "1.234,56"

export function formatMoneyGroups(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return "";
  // Use tr-TR grouping. Preserve up to 2 decimals but drop them if zero.
  const hasDecimal = Math.abs(num - Math.trunc(num)) > 1e-9;
  return num.toLocaleString("tr-TR", {
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: hasDecimal ? 2 : 0,
  });
}

export function parseMoney(input) {
  if (input === null || input === undefined) return null;
  if (typeof input === "number") return Number.isFinite(input) ? input : null;
  let s = String(input).trim();
  if (s === "") return null;
  // Strip currency symbols & letters (₺ $ € £ TL etc.)
  s = s.replace(/[₺$€£¥₽]/g, "").replace(/[A-Za-zçÇğĞıİöÖşŞüÜ]/g, "").trim();
  if (s === "" || s === "-") return null;
  // Determine decimal separator.
  const commaCount = (s.match(/,/g) || []).length;
  const dotCount = (s.match(/\./g) || []).length;
  // Heuristic:
  // - If both present, whichever comes LAST is the decimal separator.
  // - If only comma and it looks like a decimal (single ',' + trailing 1-3 digits) -> decimal
  // - If only dots and looks like decimal (single '.' + 1-2 trailing digits, no other grouping) -> decimal
  // - Otherwise treat separators as thousand-grouping and remove them.
  let normalized = s;
  if (commaCount && dotCount) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    if (lastComma > lastDot) {
      normalized = s.replace(/\./g, "").replace(",", ".");
    } else {
      normalized = s.replace(/,/g, "");
    }
  } else if (commaCount === 1) {
    const [, frac] = s.split(",");
    // Only treat as decimal separator if fraction has 1-2 digits AND no
    // spaces (Turkish decimal like "1,5" or "12,50"). Otherwise treat the
    // comma as a US-style thousand grouping (e.g. "324,000" or "1,234").
    if (frac && frac.length > 0 && frac.length <= 2 && !s.includes(" ")) {
      normalized = s.replace(",", ".");
    } else {
      normalized = s.replace(/,/g, "");
    }
  } else if (dotCount === 1) {
    const [, frac] = s.split(".");
    if (frac && frac.length > 0 && frac.length <= 2 && !/\d{4}/.test(frac)) {
      // "1234.56" or "0.5" — treat as decimal
      normalized = s;
    } else {
      normalized = s.replace(/\./g, "");
    }
  } else {
    // multiple dots or multiple commas or no separators — treat all as grouping
    normalized = s.replace(/[.,]/g, "");
  }
  // Kill any residual whitespace/nbsp
  normalized = normalized.replace(/[\s\u00A0]/g, "");
  if (normalized === "" || normalized === "-") return null;
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
}

/** Return display string for a raw input value (as user types). */
export function formatDuringTyping(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  const s = String(raw);
  // Preserve a trailing "," so the user can enter decimals.
  const hasTrailingComma = /,$/.test(s);
  const parsed = parseMoney(s);
  if (parsed === null) return s.replace(/[^\d,.\-\s₺$€£]/g, "");
  let out = formatMoneyGroups(parsed);
  if (hasTrailingComma && !out.includes(",")) out += ",";
  return out;
}
